import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, ChapterHead } from "@/components/Section";

const IMG_PRIEURE = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/8c98e760-99c9-4d28-bb70-dcc1926f9f46.jpg";
const IMG_TAPISSERIE = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/174f21dd-0309-4a80-916e-df00e3163cef.png";
const LINEAGE_IMGS = [
  IMG_TAPISSERIE,
  "https://upload.wikimedia.org/wikipedia/commons/thumb/0/03/Saintlouis.jpg/500px-Saintlouis.jpg",
  "https://upload.wikimedia.org/wikipedia/commons/b/b3/Jean_Racine_-_Versailles_MV_2926.jpg",
  "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Agence_Rol_-_Commandant_Abrial_sur_le_Tourville_01.jpg/500px-Agence_Rol_-_Commandant_Abrial_sur_le_Tourville_01.jpg",
  null,
  "/gottfried-degenfeld.webp",
  "/gilles-portrait.webp",
  "/marine-de-chassey.webp",
  null,
];

const Biography = () => {
  const { t } = useLang();
  const [showAbrialNote, setShowAbrialNote] = useState(false);
  const [showJcNote, setShowJcNote] = useState(false);
  return (
    <section id="biographie" data-testid="biography-section" className="relative px-5 md:px-10 py-28 md:py-40 scroll-mt-20">
      <div className="max-w-[1600px] mx-auto">
        <ChapterHead kicker={t.bio.kicker} title={t.bio.title} testId="biography" />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="relative overflow-hidden aspect-[3/4] border border-line shadow-[0_30px_80px_-30px_rgba(33,26,19,0.4)]">
                <img src="/gilles-portrait.webp" alt="Gilles de Buretel de Chassey" data-testid="biography-portrait" className="w-full h-full object-cover img-lumiere hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" />
              </div>
              <p data-testid="biography-portrait-caption" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted2 mt-4">
                Gilles de Buretel de Chassey
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden aspect-[4/5] border border-line shadow-[0_30px_80px_-30px_rgba(33,26,19,0.4)]">
                <img src={IMG_PRIEURE} alt="Prieuré Saint-Léonard" className="w-full h-full object-cover img-lumiere hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" />
              </div>
              <p data-testid="biography-caption" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted2 mt-4">
                {t.bio.caption}
              </p>
            </Reveal>
            <Reveal delay={0.2} className="hidden lg:block">
              <div className="relative overflow-hidden border border-line aspect-[16/9]">
                <img src={IMG_TAPISSERIE} alt="Tapisserie de Bayeux — Guillaume le Conquérant" className="w-full h-full object-cover img-lumiere transition-all duration-[1200ms]" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-6 max-w-2xl">
              {t.bio.paragraphs.map((p, i) => (
                <Reveal key={i} delay={0.1 + i * 0.08}>
                  <p data-testid={`biography-paragraph-${i}`} className="font-display text-xl md:text-2xl leading-relaxed text-ink2 first:text-ink first:text-2xl md:first:text-3xl">
                    {p}
                  </p>
                </Reveal>
              ))}
              <Reveal delay={0.4}>
                <p data-testid="biography-languages" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-terra pt-2">
                  {t.bio.languages}
                </p>
              </Reveal>
            </div>

            <div data-testid="biography-timeline" className="mt-16 border-t border-line">
              {t.bio.timeline.map((item, i) => (
                <Reveal key={i} delay={i * 0.05} y={20}>
                  <div data-testid={`timeline-item-${i}`} className="group grid grid-cols-[110px_1fr] md:grid-cols-[170px_1fr] gap-4 md:gap-8 py-5 border-b border-line hover:bg-parchment/60 transition-colors px-2 md:px-4 -mx-2 md:-mx-4">
                    <span className="font-mono2 text-[11px] md:text-xs uppercase tracking-[0.15em] text-terra pt-1">{item.year}</span>
                    <div>
                      <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-terra transition-colors">{item.title}</h3>
                      <p className="text-sm text-muted2 mt-1 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-16 md:mt-20">
          <div data-testid="bio-lineage" className="border border-line bg-white/50 p-6 md:p-10">
            <p data-testid="bio-lineage-title" className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-terra mb-8">{t.bio.lineage.title}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-x-4 gap-y-8">
              {t.bio.lineage.figures.map((f, i) => {
                const isNote = i === 3 || i === 4;
                const isOpen = i === 3 ? showAbrialNote : showJcNote;
                const toggle = i === 3 ? () => setShowAbrialNote(!showAbrialNote) : () => setShowJcNote(!showJcNote);
                const toggleLabel = i === 3 ? t.bio.lineage.noteToggle : t.bio.lineage.jcToggle;
                const Tag = isNote ? "button" : "div";
                return (
                  <Tag
                    key={i}
                    data-testid={i === 3 ? "lineage-abrial-toggle" : i === 4 ? "lineage-jc-toggle" : `lineage-figure-${i}`}
                    {...(isNote
                      ? { onClick: toggle, "aria-pressed": isOpen, "aria-label": toggleLabel, type: "button" }
                      : {})}
                    className={`relative text-center${isNote ? " cursor-pointer" : ""}`}
                  >
                    <div className={`relative w-20 h-20 md:w-24 md:h-24 mx-auto rounded-full overflow-hidden border-2 shadow-md mb-4 transition-colors ${isNote ? (isOpen ? "border-terra" : "border-gold/50 group-hover:border-terra") : "border-gold/50"}`}>
                      {LINEAGE_IMGS[i] ? (
                        <img src={LINEAGE_IMGS[i]} alt={f.name} className="w-full h-full object-cover img-lumiere" loading="lazy" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center bg-parchment font-display text-3xl text-terra select-none" aria-hidden="true">
                          {f.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    {i < t.bio.lineage.figures.length - 1 && (
                      <span aria-hidden="true" className="hidden md:block absolute top-9 md:top-11 start-full -translate-x-1/2 text-gold/60 font-display italic text-2xl select-none">→</span>
                    )}
                    <h4 className="font-display text-lg text-ink leading-tight">{f.name}</h4>
                    <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-terra mt-1">{f.dates}</p>
                    <p className="text-muted2 text-xs mt-1.5 leading-snug">{f.role}</p>
                    {isNote && (
                      <span className={`inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-[0.2em] mt-2 transition-colors ${isOpen ? "text-terra" : "text-gold"}`}>
                        <Plus size={11} className={`transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
                        {toggleLabel}
                      </span>
                    )}
                  </Tag>
                );
              })}
            </div>
            <AnimatePresence>
              {showAbrialNote && (
                <motion.div
                  data-testid="lineage-abrial-note"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.4 }}
                  className="mt-8 border-t border-line pt-6 max-w-3xl mx-auto"
                >
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-terra mb-3">{t.bio.lineage.noteToggle}</p>
                  <p className="text-ink2 text-sm leading-relaxed">{t.bio.lineage.note}</p>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {showJcNote && (
                <motion.div
                  data-testid="lineage-jc-note"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.4 }}
                  className="mt-8 border-t border-line pt-6 max-w-3xl mx-auto"
                >
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-terra mb-3">{t.bio.lineage.jcToggle}</p>
                  <p className="text-ink2 text-sm leading-relaxed">{t.bio.lineage.jcNote}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Biography;
